import Address from '../../models/Address';
import {
    CANADIAN_PROVINCES,
    COUNTRIES,
    COUNTRIES_AREA_CODES,
    COUNTRIES_NAMES,
    PROVINCES_BY_COUNTRY,
    US_STATES,
    CountryCode,
    UsStateName,
    CanadianProvinceName,
} from '../../scripts/constants';

export function useAddress () {

    function getAddressLine1 (address: Address | null): string {

        if (address === null) {
            return '';
        }

        let line1 = '';
        const street1 = address.street1 ? address.street1 : '';
        const street2 = address.street2 ? address.street2 : '';

        if (street1) {
            line1 += street1;
        }
        if (street2) {
            line1 += `${line1 ? ', ' + street2 : street2}`;
        }

        return line1;
    }

    function getProvinceStateArray (country: string): Record<string, string> | null {
        const hasRecord = country in PROVINCES_BY_COUNTRY;
        return hasRecord ? PROVINCES_BY_COUNTRY[country] : null;
    }

    function getAddressLine2 (address: Address | null): string {

        if (address === null) {
            return '';
        }

        let line2 = '';
        const city = address.city ? address.city : '';
        const province = address.province ? address.province : '';
        const postalCode = address.postalCode ? address.postalCode : '';
        const country = address.country ? address.country : '';

        if (city) {
            line2 += city;
        }
        if (postalCode) {
            line2 += `${line2 ? ', ' + postalCode : postalCode}`;
        }
        if (province) {
            line2 += `${line2 ? ', ' + province : province}`;
        }
        if (country) {
            const countryName = COUNTRIES_NAMES[country] ? COUNTRIES_NAMES[country] : country;
            line2 += `${line2 ? ', ' + countryName : countryName}`;
        }

        return line2;
    }

    function getAddressLine3 (address: Address | null): string {

        if (address === null) {
            return '';
        }

        let line3 = '';
        const phone = address.phone ? formatContactNumbers(address.phone) : '';
        const fax = address.fax ? formatContactNumbers(address.fax) : '';

        if (phone) {
            line3 += 'Phone: ' + phone;
            line3 += fax ? ' ' : '';
        }
        if (fax) {
            line3 += 'Fax: ' + fax;
        }

        return line3;
    }

    function getMainAddressLine (address: Address): string {
        let mainAddress = getAddressLine1(address);
        const addressLine2 = getAddressLine2(address);

        if (mainAddress !== '' && addressLine2 !== '') {
            mainAddress += ', ' + addressLine2;
        }
        return mainAddress;
    }

    function shippingSameAsBilling (billingAddress: Address, shippingAddress: Address): boolean {
        let key: keyof Address;

        for (key in (new Address)) {
            if (key === 'id') { continue; }
            const billingValue = billingAddress[key];
            const shippingValue = shippingAddress[key];
            if (shippingValue !== billingValue) {
                return false;
            }
        }
        return true;
    }

    function isAddressEmpty (address: Address): boolean {
        let key: keyof Address;
        const dateFields = new Set(['dateCreated', 'dateUpdated']);

        for (key in address) {
            if (typeof address[key] === 'string' && address[key] !== '') {
                if (dateFields.has(key) && address[key] === '00-00-00 00:00:00') {
                    continue;
                }
                return false;
            }
        }
        return true;
    }

    function formatContactNumbers (contactNumber: string): string {

        const numberRegex = /\D/;
        const formatReqexp = /^(\d{3})(\d{3,4})(\d{4})$/;
        const replacement = '$1-$2-$3';
        const standardLength = 10;

        let countryCode = '';

        let cleanNumber = contactNumber.replace(numberRegex, '');

        if (cleanNumber.length !== standardLength) {

            countryCode = getCountryCode(cleanNumber, standardLength);
            cleanNumber = getNumberPart(cleanNumber, standardLength);

        }

        return countryCode + cleanNumber.replace(formatReqexp, replacement);

    }

    function getCountryCode (cleanNumber: string, standardLength: number): string {

        const reverseClean = reverseString(cleanNumber);
        const numberPart = reverseClean.substr(0, standardLength);

        let countryCode = reverseClean.replace(numberPart, '');

        countryCode = reverseString(countryCode);
        if (countryCode !== '') {
            countryCode = '+' + countryCode;
        }

        return countryCode;
    }

    function getNumberPart (cleanNumber: string, standardLength: number): string {
        const reverseClean = reverseString(cleanNumber);
        const numberPart = reverseClean.substr(0, standardLength);
        return reverseString(numberPart);
    }

    function reverseString (str: string): string {
        return str.split('').reverse().join('');
    }

    const displayStreetCityPostalCode = (address: Address): string => {
        const addressArray = [];
        if (address.street1 !== '') {
            addressArray.push(address.street1);
        }
        if (address.city !== '') {
            addressArray.push(address.city);
        }
        if (address.postalCode !== '') {
            addressArray.push(address.postalCode);
        }
        return addressArray.join(', ');
    };

    const displayProvinceCountry = (address: Address): string => {
        const addressArray = [];
        if (address.province !== '') {
            addressArray.push(address.province);
        }
        if (address.country !== '') {
            addressArray.push(address.country);
        }
        return addressArray.join(', ');
    };

    function isValidProvince(province: string): province is CanadianProvinceName {
        return Object.values(CANADIAN_PROVINCES).includes(province);
    }

    function isValidState(state: string): state is UsStateName {
        return Object.values(US_STATES).includes(state);
    }

    function isValidCountry(country: string): country is CountryCode {
        return Object.values(COUNTRIES).includes(country);
    }

    function isCanada(country: string): boolean {
        return country === COUNTRIES.CANADA;
    }

    function isUs(country: string): boolean {
        return country === COUNTRIES.UNITED_STATES;
    }

    const displayPhoneNumber = (address: Address) => {
        const ext = COUNTRIES_AREA_CODES[address.country];
        const cleaned = ('' + address.phone).replace(/\D/g, '');
        const match = cleaned.match(/^[+]?(\d{1,3})?(-)?(\d{1,3})?(\d{3})(\d{3,4})(\d{4})$/);
        if (match) {
            const lineNumber = match.pop();
            const telPrefix = match.pop();
            const areaCode = match.pop();
            const countryCode = ext ? `+${ext} ` : '';
            return `${countryCode}(${areaCode})-${telPrefix}-${lineNumber}`;
        }
        return '';
    };

    const validateCanadianPostalCode = (code: string) => {
        return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(code);
    };

    const validateUsZipCode = (code: string) => {
        return /^\d{5}(?:[ -]?\d{4})?$/.test(code);
    };

    const validatePostalCode = (address: Address) => {
        if(isCanada(address.country)) { return validateCanadianPostalCode(address.postalCode); }
        if(isUs(address.country)) { return validateUsZipCode(address.postalCode); }
        return true;
    };

    const validateEmail = (email: string) => {
        return /^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/.test(email);
    };

    const validatePhoneNumber = (number: string) => {
        return /^\d{10}$/.test(number);
    };

    const validateAddress = (address: Address, hasFullName = true, checkPhone = true) => {
        if(!address.country) { return false; }
        if(!address.contactName && hasFullName) { return false; }
        if(!address.street1) { return false; }
        if(!address.city) { return false; }
        if(!address.province) { return false; }
        if(!address.postalCode) { return false; }
        if(!address.email) { return false; }
        if(!address.phone) { return false; }
        if(!validateEmail(address.email)) { return false; }
        if(!validatePostalCode(address)) { return false; }
        return !checkPhone || validatePhoneNumber(address.phone);
    };

    const formatPostal = (address: Address) => {
        if(isCanada(address.country) && validateCanadianPostalCode(address.postalCode)) {
            const formattedPostalCode = address.postalCode.length === 6 ? address.postalCode.slice(0, 3) + ' ' + address.postalCode.slice(3) : address.postalCode;
            return formattedPostalCode.toUpperCase();
        }
        if(isUs(address.country) && validateUsZipCode(address.postalCode)) {
            return address.postalCode.length === 9 ? address.postalCode.slice(0, 5) + '-' + address.postalCode.slice(5) : address.postalCode;
        }
        return address.postalCode;
    };

    const formatPhoneNumber = (phoneNumber: string) => {
        if(phoneNumber.length < 3) {
            return phoneNumber;
        }
        if(phoneNumber.length === 3) {
            return phoneNumber + '-';
        }
        if(phoneNumber.length < 6) {
            return phoneNumber.slice(0, 3) + '-' + phoneNumber.slice(3);
        }
        if(phoneNumber.length === 6) {
            return phoneNumber.slice(0, 3) + '-' + phoneNumber.slice(3) + '-';
        }
        return phoneNumber.slice(0, 3) + '-' + phoneNumber.slice(3, 6) + '-' + phoneNumber.slice(6, 10);
    };

    return {
        isValidProvince,
        isValidState,
        isValidCountry,
        isCanada,
        isUs,
        getAddressLine1,
        getAddressLine2,
        getProvinceStateArray,
        getMainAddressLine,
        getAddressLine3,
        shippingSameAsBilling,
        isAddressEmpty,
        displayStreetCityPostalCode,
        displayProvinceCountry,
        displayPhoneNumber,
        validatePostalCode,
        validateEmail,
        validatePhoneNumber,
        validateAddress,
        formatPostal,
        formatPhoneNumber,
    };
}
